import type { ReactNode } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  Separator,
  Skeleton,
} from '@eureca/ui';
import type { EvaluationCardData } from './candidate-evaluations';
import {
  EvaluationsEmpty,
  EvaluationsError,
  EvaluationsSkeleton,
  EvaluationsTimeline,
} from './EvaluationsTimeline';
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  CopyIcon,
  GraduationCapIcon,
  LinkedinIcon,
  MailIcon,
  PhoneIcon,
} from './icons';

/** Contatos copiáveis (cabeçalho + linhas inline de "Dados pessoais"). */
export type CopyKind = 'email' | 'phone' | 'proLink';

/** EU-686 (RN-01) — abas do perfil; Dados pessoais é a default. */
export type ProfileTabId = 'personal' | 'history';

const PROFILE_TABS: { id: ProfileTabId; label: string }[] = [
  { id: 'personal', label: 'Dados pessoais' },
  { id: 'history', label: 'Histórico de avaliações' },
];

// EU-686 desligado até o QA passar (bugs abertos: refetch em loop na aba
// Histórico e 404 fantasma de candidato inexistente).
// Reativar: trocar para true e remover o .skip dos testes das abas.
const EU686_TABS_ENABLED = true;

/**
 * Linha "label: valor" dos blocos da coluna principal. REGRA DE OURO
 * (PFC-005): campo null/vazio NÃO vira linha — o container só monta linhas
 * de valores presentes; aqui nada renderiza traço/placeholder.
 */
export interface ProfileRow {
  label: string;
  value: string;
  /** Presente → ícone de copiar inline após o valor. */
  copyKind?: CopyKind;
}

export interface FormationCardData {
  id: string;
  /** courseName ?? courseTypeName. */
  title: string;
  /** Linhas já filtradas (courseTypeName / instituição / data de formação). */
  lines: string[];
}

/** Precedência resolvida no container: cancelled → rejected → jobClosed → ativa. */
export type JourneyBadgeKind = 'active' | 'cancelled' | 'rejected' | 'closed';

export interface JourneyItemData {
  subscriptionId: string;
  jobName: string;
  programName: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  badge: JourneyBadgeKind;
  /** dd/mm/aaaa — null esconde a linha. */
  subscribedAt: string | null;
  /** "{fase} / {Finalizada|Em andamento|Não realizada}" — null esconde. */
  phaseLine: string | null;
  /** "Sim / Não". */
  qualifiedAptLine: string;
  /** "{title} | {cidades - UFs}" — null esconde. */
  workAreaLine: string | null;
}

export interface CandidateProfilePageProps {
  /** Loading/erro da query de PERFIL (página inteira — PFC-031). */
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onBack: () => void;

  /** user.socialName ?? user.name. */
  displayName: string;
  avatarUrl: string | null;
  initials: string;
  hasResume: boolean;
  onOpenResume: () => void;

  /** Valores de exibição dos ícones de contato — null esconde o ícone (PFC-010). */
  email: string | null;
  phone: string | null;
  proLink: string | null;
  onCopy: (kind: CopyKind) => void;

  personalRows: ProfileRow[];
  addressRows: ProfileRow[];
  diversityRows: ProfileRow[];
  /** Laudo (PFC-011) — só renderiza o link quando true. */
  hasAttestation: boolean;
  onOpenAttestation: () => void;
  formations: FormationCardData[];

  /** Card "Trajetória na Eureca" — estados PRÓPRIOS (PFC-016). */
  journeyLoading: boolean;
  journeyError: boolean;
  onJourneyRetry: () => void;
  journeyItems: JourneyItemData[];

  /** EU-686 — abas (RN-01..04): a troca só alterna a coluna de conteúdo. */
  activeTab: ProfileTabId;
  onTabChange: (tab: ProfileTabId) => void;
  /** Aba Histórico — estados PRÓPRIOS restritos à coluna (RN-14..17). */
  evaluationsLoading: boolean;
  evaluationsError: boolean;
  evaluationCards: EvaluationCardData[];
}

/**
 * Tela "Perfil do Candidato" (EU-594) — Figma "Programs" node 15539:56326.
 * Coluna principal (~2/3, blocos label:valor) + card lateral sticky (~1/3).
 * Puro: sem hooks, sem router — o container injeta dados e handlers.
 */
export function CandidateProfilePage({
  isLoading,
  isError,
  onRetry,
  onBack,
  displayName,
  avatarUrl,
  initials,
  hasResume,
  onOpenResume,
  email,
  phone,
  proLink,
  onCopy,
  personalRows,
  addressRows,
  diversityRows,
  hasAttestation,
  onOpenAttestation,
  formations,
  journeyLoading,
  journeyError,
  onJourneyRetry,
  journeyItems,
  activeTab,
  onTabChange,
  evaluationsLoading,
  evaluationsError,
  evaluationCards,
}: CandidateProfilePageProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-card">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 py-8 md:px-10">
        <div>
          <button
            type="button"
            data-testid="candidate-profile-back"
            onClick={onBack}
            className="inline-flex flex-row items-center gap-1.5 rounded-sm text-sm font-medium text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <ArrowLeftIcon className="size-4" />
            Voltar
          </button>
        </div>

        {isLoading ? (
          <PageSkeleton />
        ) : isError ? (
          <PageError onRetry={onRetry} />
        ) : (
          <>
            <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-row items-center gap-4">
                {/* 64px, fallback de iniciais. */}
                <Avatar size="2xl">
                  {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-1">
                  <span className="text-sm leading-none text-muted-foreground">
                    Perfil do candidato(a)
                  </span>
                  <h1 className="text-3xl font-medium leading-tight tracking-tight text-foreground md:text-4xl">
                    {displayName}
                  </h1>
                </div>
              </div>

              <div className="flex flex-row items-center gap-2">
                {hasResume ? (
                  <Button data-testid="candidate-profile-open-resume" onClick={onOpenResume}>
                    Acessar currículo
                  </Button>
                ) : null}
                {email ? (
                  <ContactIconButton
                    testId="candidate-profile-copy-email"
                    label="Copiar e-mail"
                    Icon={MailIcon}
                    onClick={() => onCopy('email')}
                  />
                ) : null}
                {phone ? (
                  <ContactIconButton
                    testId="candidate-profile-copy-phone"
                    label="Copiar telefone"
                    Icon={PhoneIcon}
                    onClick={() => onCopy('phone')}
                  />
                ) : null}
                {/* LinkedIn: só com proLink presente (PFC-010). */}
                {proLink ? (
                  <ContactIconButton
                    testId="candidate-profile-copy-prolink"
                    label="Copiar link do LinkedIn"
                    Icon={LinkedinIcon}
                    onClick={() => onCopy('proLink')}
                  />
                ) : null}
              </div>
            </header>

            {/* EU-686 (RN-01/03) — abas abaixo do cabeçalho; só a coluna de
                conteúdo alterna (cabeçalho, Voltar e Trajetória persistem).
                Com EU686_TABS_ENABLED=false a barra some e a página volta ao
                comportamento da EU-594 (Dados pessoais direto). */}
            {EU686_TABS_ENABLED ? (
              <nav
                role="tablist"
                aria-label="Seções do perfil"
                className="inline-flex w-fit flex-row gap-1 rounded-lg bg-secondary p-1"
              >
                {PROFILE_TABS.map((tab) => {
                  const selected = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      data-testid={`candidate-profile-tab-${tab.id}`}
                      onClick={() => onTabChange(tab.id)}
                      className={cn(
                        'rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                        selected
                          ? 'border border-primary/30 bg-card text-foreground shadow-xs'
                          : 'border border-transparent text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            ) : null}

            <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3">
              {/* Coluna principal (~2/3). */}
              <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
                {EU686_TABS_ENABLED && activeTab === 'history' ? (
                  evaluationsLoading ? (
                    <EvaluationsSkeleton />
                  ) : evaluationsError ? (
                    <EvaluationsError />
                  ) : evaluationCards.length === 0 ? (
                    <EvaluationsEmpty />
                  ) : (
                    <EvaluationsTimeline cards={evaluationCards} />
                  )
                ) : (
                  <>
                    {personalRows.length > 0 ? (
                      <section className="flex flex-col gap-5">
                        <h2 className="text-2xl font-medium leading-none tracking-tight text-foreground">
                          Dados pessoais
                        </h2>
                        <RowsGrid rows={personalRows} onCopy={onCopy} />
                      </section>
                    ) : null}

                    {/* Endereço — sem título no Figma; separado por Separator. */}
                    {addressRows.length > 0 ? (
                      <>
                        <Separator />
                        <section>
                          <RowsGrid rows={addressRows} onCopy={onCopy} />
                        </section>
                      </>
                    ) : null}

                    {/* Diversidade — idem; o laudo entra como linha-link (PFC-011). */}
                    {diversityRows.length > 0 || hasAttestation ? (
                      <>
                        <Separator />
                        <section>
                          <RowsGrid
                            rows={diversityRows}
                            onCopy={onCopy}
                            trailing={
                              hasAttestation ? (
                                <div className="flex items-baseline justify-between gap-6">
                                  <span className="shrink-0 text-sm leading-5 text-muted-foreground">
                                    Laudo
                                  </span>
                                  <button
                                    type="button"
                                    data-testid="candidate-profile-open-attestation"
                                    onClick={onOpenAttestation}
                                    className="inline-flex shrink-0 flex-row items-center gap-1 rounded-sm text-sm font-medium text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                  >
                                    Acessar laudo
                                    <ArrowUpRightIcon className="size-3.5" />
                                  </button>
                                </div>
                              ) : null
                            }
                          />
                        </section>
                      </>
                    ) : null}

                    {formations.length > 0 ? (
                      <section className="flex flex-col gap-5 pt-4">
                        <h2 className="text-2xl font-medium leading-none tracking-tight text-foreground">
                          Formação
                        </h2>
                        <FormationsGrid formations={formations} />
                      </section>
                    ) : null}
                  </>
                )}
              </div>

              {/* Card lateral sticky (~1/3) — rolagem interna própria (PFC-013/014). */}
              <aside
                data-testid="candidate-profile-journey"
                className="flex max-h-[calc(100dvh-4rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs lg:sticky lg:top-8"
              >
                <h2 className="px-6 pb-3 pt-6 text-2xl font-medium leading-none tracking-tight text-foreground">
                  Trajetória na Eureca
                </h2>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-6">
                  {journeyLoading ? (
                    <JourneySkeleton />
                  ) : journeyError ? (
                    <JourneyError onRetry={onJourneyRetry} />
                  ) : journeyItems.length === 0 ? (
                    <JourneyEmpty />
                  ) : (
                    journeyItems.map((item, index) => (
                      <JourneyItem
                        key={item.subscriptionId}
                        item={item}
                        withSeparator={index > 0}
                      />
                    ))
                  )}
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ContactIconButton({
  testId,
  label,
  Icon,
  onClick,
}: {
  testId: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex size-9 items-center justify-center rounded-md border border-input bg-card text-foreground',
        'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

/**
 * Grid 2 colunas de linhas "label: valor" — label à esquerda, valor em
 * destaque à direita (Figma). Só recebe linhas com valor (PFC-005).
 */
function RowsGrid({
  rows,
  onCopy,
  trailing,
}: {
  rows: ProfileRow[];
  onCopy: (kind: CopyKind) => void;
  trailing?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-6">
          <span className="shrink-0 text-sm leading-5 text-muted-foreground">{row.label}</span>
          <span className="flex min-w-0 flex-row items-center gap-1.5">
            <span className="min-w-0 truncate text-right text-sm font-medium leading-5 text-foreground">
              {row.value}
            </span>
            {row.copyKind ? (
              <button
                type="button"
                data-testid={`candidate-profile-inline-copy-${row.copyKind.toLowerCase()}`}
                aria-label={`Copiar ${row.label.toLowerCase()}`}
                title={`Copiar ${row.label.toLowerCase()}`}
                onClick={() => onCopy(row.copyKind as CopyKind)}
                className="shrink-0 rounded-sm text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <CopyIcon className="size-4" />
              </button>
            ) : null}
          </span>
        </div>
      ))}
      {trailing}
    </div>
  );
}

/** Grid 2 colunas de cards de formação, com Separator entre pares (Figma). */
function FormationsGrid({ formations }: { formations: FormationCardData[] }) {
  const pairs: FormationCardData[][] = [];
  for (let i = 0; i < formations.length; i += 2) {
    pairs.push(formations.slice(i, i + 2));
  }
  return (
    <div className="flex flex-col gap-6">
      {pairs.map((pair, pairIndex) => (
        <div key={pair[0].id} className="flex flex-col gap-6">
          {pairIndex > 0 ? <Separator /> : null}
          <div className="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
            {pair.map((formation) => (
              <article key={formation.id} className="flex flex-row items-start gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                  <GraduationCapIcon className="size-3.5" />
                </span>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <h3 className="truncate text-base font-medium leading-tight text-foreground">
                    {formation.title}
                  </h3>
                  {formation.lines.map((line) => (
                    <p key={line} className="truncate text-sm leading-5 text-muted-foreground">
                      {line}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function JourneyItem({ item, withSeparator }: { item: JourneyItemData; withSeparator: boolean }) {
  return (
    <article className="flex flex-col gap-2">
      {withSeparator ? <Separator className="mb-2" /> : null}
      <div className="flex flex-row items-start gap-2.5">
        {/* Logo da empresa — 32px, cantos arredondados, fallback de inicial. */}
        <Avatar size="sm" shape="square">
          {item.companyLogoUrl ? <AvatarImage src={item.companyLogoUrl} alt="" /> : null}
          <AvatarFallback>
            {(item.companyName ?? item.jobName).charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="truncate text-sm font-semibold leading-5 text-foreground">
            {item.jobName}
          </h3>
          {item.programName ? (
            <p className="truncate text-xs leading-4 text-muted-foreground">{item.programName}</p>
          ) : null}
        </div>
        <JourneyStatusBadge badge={item.badge} />
      </div>
      <div className="flex flex-col gap-1">
        {item.subscribedAt ? (
          <JourneyLine label="Inscrição feita" value={item.subscribedAt} />
        ) : null}
        {item.phaseLine ? <JourneyLine label="Fase/Status" value={item.phaseLine} /> : null}
        <JourneyLine label="Qualificado/Apto" value={item.qualifiedAptLine} />
        {item.workAreaLine ? (
          <JourneyLine label="Área de atuação" value={item.workAreaLine} />
        ) : null}
      </div>
    </article>
  );
}

function JourneyLine({ label, value }: { label: string; value: string }) {
  return (
    <p className="text-xs leading-4 text-muted-foreground">
      {label}: <span className="font-medium text-foreground">{value}</span>
    </p>
  );
}

/**
 * Precedência (EU-594): Desistência > Reprovado > Encerrada > Ativa. Sem token
 * "info" no design system, "Ativa" segue a convenção do StatusBadge da Gestão
 * de vaga (`bg-secondary text-primary`); "Encerrada" fica cinza via `muted`.
 */
function JourneyStatusBadge({ badge }: { badge: JourneyBadgeKind }) {
  if (badge === 'cancelled') {
    return <Badge className="bg-warning text-warning-foreground">Desistência</Badge>;
  }
  if (badge === 'rejected') {
    return <Badge variant="destructive">Reprovado</Badge>;
  }
  if (badge === 'closed') {
    return <Badge className="bg-muted text-muted-foreground">Encerrada</Badge>;
  }
  return <Badge className="bg-secondary text-primary">Ativa</Badge>;
}

function PageSkeleton() {
  return (
    <div data-testid="candidate-profile-loading" className="flex flex-col gap-8" aria-hidden>
      <div className="flex flex-row items-center gap-4">
        <Skeleton className="size-16 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-72" />
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    </div>
  );
}

function PageError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24">
      <p className="text-lg font-medium text-foreground">
        Não foi possível carregar o perfil do candidato
      </p>
      <button
        type="button"
        data-testid="candidate-profile-error-retry"
        onClick={onRetry}
        className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Tentar novamente
      </button>
    </div>
  );
}

function JourneySkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-24 w-full rounded-xl" />
      ))}
    </>
  );
}

/** Vazio ≠ erro (PFC-016): sem candidaturas é um estado legítimo. */
function JourneyEmpty() {
  return (
    <p data-testid="candidate-profile-journey-empty" className="text-sm text-muted-foreground">
      Esse candidato ainda não fez nenhuma candidatura.
    </p>
  );
}

function JourneyError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-sm text-destructive">Falha ao carregar a trajetória do candidato.</p>
      <button
        type="button"
        data-testid="candidate-profile-journey-retry"
        onClick={onRetry}
        className="rounded-sm text-sm font-medium text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        Tentar novamente
      </button>
    </div>
  );
}
